import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useApp } from '../context/AppContext';

const DeleteAccountPage = () => {
  const { user, isAuthenticated } = useApp();
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/account/delete-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: isAuthenticated ? user?.email : email,
          reason: reason,
          user_id: user?.id || null
        }),
      });

      if (response.ok) {
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting deletion request:', error);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout title="Request Submitted" showBack>
        <div className="bg-white px-4 py-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Request Received</h2>
            <p className="text-gray-600 mb-6">
              We have received your account deletion request. Our team will process your request within 30 days and send you a confirmation email once completed.
            </p>
            <p className="text-sm text-gray-500">
              If you have any questions, please contact us at support@mintedbuysell.com.au
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Delete Account" showBack>
      <div className="bg-white px-4 py-6">
        <div className="max-w-md mx-auto">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Request Account Deletion</h2>
            <p className="text-gray-600 text-sm">
              We're sorry to see you go. Submitting this form will request the deletion of your Minted account and all associated data.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-amber-800 mb-2">What will be deleted:</h3>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>• Your account profile and personal information</li>
              <li>• All your product listings</li>
              <li>• Your message history</li>
              <li>• Your purchase and sales history</li>
              <li>• Your wallet and transaction data</li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-blue-800 mb-2">Please note:</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• This action cannot be undone</li>
              <li>• Processing may take up to 30 days</li>
              <li>• You will receive a confirmation email</li>
              <li>• Some data may be retained for legal purposes</li>
            </ul>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isAuthenticated && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your account email"
                  required
                />
              </div>
            )}

            {isAuthenticated && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  Logged in as: <span className="font-medium">{user?.email}</span>
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for leaving (optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Help us improve by sharing why you're leaving..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-minted-500 focus:border-transparent"
                rows={3}
              />
            </div>

            <Button
              type="submit"
              disabled={loading || (!isAuthenticated && !email)}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? 'Submitting...' : 'Request Account Deletion'}
            </Button>
          </form>

          <p className="text-xs text-gray-500 text-center mt-6">
            By submitting this request, you confirm that you want to permanently delete your Minted account. For questions, contact support@mintedbuysell.com.au
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default DeleteAccountPage;
